import Label from 'components/Label';
import Image from 'next/image';
import React, { useEffect } from 'react';
import { JsonParam, useQueryParam } from 'use-query-params';

const PcModelPanelPrintsOptionsList = ({
  options,
  handleSelectOption,
  selectedOption,
  panelPrintSubGroups,
  suggestedOptionId,
}) => {
  const [selections, setLocalSelections] = useQueryParam(
    'selections',
    JsonParam,
  );

  useEffect(() => {
    if (selections && selections.panel_print) {
      const selectedOption = options.find(
        (option) => option.variant_id === selections.panel_print,
      );
      if (selectedOption) {
        handleSelectOption(selectedOption);
      }
    }
  }, [selections, options, handleSelectOption]);

  const handlePrintChange = (option) => {
    const updatedSelections = { ...selections };
    updatedSelections['panel_print'] = option.variant_id;
    setLocalSelections(updatedSelections);
  };

  const uniqueSubGroups = panelPrintSubGroups.reduce((acc, subGroup) => {
    acc[subGroup.option_sub_group_id] = subGroup.sub_group_title;
    return acc;
  }, {});

  const groupedOptions = options.reduce(
    (acc, option) => {
      const subGroupId = option.option_sub_group_id || 'Panel Prints';
      if (!acc[subGroupId]) {
        acc[subGroupId] = [];
      }
      acc[subGroupId].push(option);
      return acc;
    },
    { 'Panel Prints': [] },
  );

  const groupedOptionsArray = Object.keys(groupedOptions).map((subGroupId) => {
    const subGroupTitle =
      subGroupId === 'Panel Prints'
        ? 'Panel Prints'
        : uniqueSubGroups[subGroupId] || 'Unknown Subgroup';
    return {
      subGroupId,
      subGroupTitle,
      options: groupedOptions[subGroupId],
    };
  });

  return (
    <>
      <div className="panel-prints-options">
        <p className="main-title">Chassis Customisation</p>
        <p className="action-title">Pick a Design</p>
        <div className="groups-wrapper">
          {groupedOptionsArray.map((group) => (
            <div key={group.subGroupId}>
              <p className="sub-title">{group.subGroupTitle}</p>
              <div className="panel-prints-options-wrapper">
                {group.options.map((option) => (
                  <div
                    className={`panel-print`}
                    key={option.id}
                    onClick={() => {
                      handleSelectOption(option);
                      handlePrintChange(option);
                    }}
                  >
                    <div
                      className={`panel-print-image ${
                        selectedOption.id === option.id ? 'active-option' : ''
                      }`}
                    >
                      <Image
                        src={option.image.src}
                        layout="fill"
                        objectFit="contain"
                        alt={option.title}
                      />
                    </div>
                    <div className="panel-print-text-wrapper">
                      <p className="panel-print-title">{option.title}</p>
                      {suggestedOptionId === option.option_id && (
                        <div className="suggested-look-wrapper">
                          <Label type="purple" text="Suggested Look" />
                        </div>
                      )}
                      <p
                        className={`panel-print-price ${
                          selectedOption.id === option.id
                            ? 'active-option-price'
                            : ''
                        }`}
                      >
                        {`${selectedOption.id === option.id ? '+' : ''}$`}{' '}
                        {option.price}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        .suggested-look-wrapper {
          position: relative;
          top: -2px;
          left: -2px;
        }
        .panel-prints-options {
          display: flex;
          flex-direction: column;
          width: 40%;
          min-height: 100vh;
          padding: 15px 0 15px 15px;
          background-color: #1a1a1a;
          margin-top: -72px;
          padding-top: 72px;
          padding-bottom: 30px;
          margin-bottom: -30px;
        }
        .groups-wrapper {
          width: 100%;
          height: 750px;
          overflow-y: scroll;
          overflow-x: hidden;
        }
        .panel-prints-options-wrapper {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }
        .panel-print {
          width: 123px;
          height: auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          border: 1px solid transparent;
          overflow: hidden;
          transition:
            border 0.3s ease,
            transform 0.3s ease;
        }
        .panel-print-image {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 10px;
          transition: transform 0.3s ease;
        }
        .panel-print-text-wrapper {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          text-transform: uppercase;
          width: 100%;
          height: 70px;
        }
        .panel-print-title {
          font-family: 'big_noodle_titling';
          text-align: center;
          font-size: 16px;
          font-weight: 400;
          line-height: 17.65px;
          margin: 5px 0 0;
        }
        .panel-print-price {
          font-family: 'big_noodle_titling';
          text-align: center;
          font-size: 16px;
          font-weight: 400;
          line-height: 13.24px;
          margin: 0;
        }
        .main-title {
          font-family: 'big_noodle_titling';
          font-size: 36px;
          font-weight: 400;
          line-height: 39.74px;
          text-align: left;
          margin: 0;
        }
        .action-title {
          font-family: 'big_noodle_titling';
          font-size: 20px;
          font-weight: 400;
          line-height: 22.06px;
          text-align: left;
          color: #989898;
          margin: 0 0 20px;
        }
        .sub-title {
          font-family: 'big_noodle_titling';
          font-size: 24px;
          font-weight: 400;
          line-height: 26.47px;
          text-align: left;
          text-decoration: underline;
          margin: 15px 0;
        }
        .sub-title-mobile {
          display: none;
          font-family: 'big_noodle_titling';
          font-size: 24px;
          font-weight: 400;
          line-height: 26.47px;
          text-align: left;
          text-decoration: underline;
          margin: 15px 0;
        }
        .active-option {
          border-radius: 10px;
          border: 1.5px solid #fd0300;
        }
        .active-option-price {
          color: #7ed321;
        }
        @media (max-width: 900px) {
          .panel-prints-options {
            width: 100%;
            min-height: 100%;
            align-items: flex-start;
            padding: 10px;
            margin-left: -30px;
            margin-right: -30px;
            padding: 20px 20px 0;
            margin-top: 20px;
            border-radius: 30px 30px 0 0;
          }
          .suggested-look-wrapper {
            position: relative;
            top: -2px;
            left: -6px;
          }
          .panel-prints-options-wrapper {
            width: 100%;
            justify-content: start;
            align-items: flex-start;
            text-align: left;
          }
          .groups-wrapper {
            height: 400px;
          }
          .panel-print {
            width: 100%;
            flex-direction: row;
            justify-content: start;
            gap: 10%;
            padding-left:;
          }
          .panel-print-image {
            width: 100%;
            height: 100px;
            max-width: 100px;
          }
          .panel-print-text-wrapper {
            width: 100%;
            align-items: flex-start;
            text-align: left;
          }
          .main-title,
          .action-title,
          .sub-title {
            text-align: left;
          }
          .sub-title {
            // display: none;
          }
          .sub-title-mobile {
            display: block;
          }
        }
        @media (max-width: 430px) {
          .panel-prints-options {
            align-items: flex-start;
            padding: 20px 20px 0;
            margin-top: 20px;
            border-radius: 30px 30px 0 0;
          }
          // .panel-prints-options-wrapper {
          //   width: 100%;
          //   max-height: 500px;
          //   overflow-y: scroll;
          // }
          .groups-wrapper {
            height: 300px;
          }
          .panel-print {
            width: 100%;
            flex-direction: row;
            justify-content: start;
            gap: 10%;
          }
          .panel-print-image {
            width: 100%;
            height: 80px;
            max-width: 80px;
          }
          .panel-print-title.panel-print-price {
            width: 100%;
          }
          .main-title,
          .action-title,
          .sub-title {
            text-align: left;
          }
        }
      `}</style>
    </>
  );
};

export default PcModelPanelPrintsOptionsList;
